<script>
import {
  iDrawer,
  iButton,
  iButtonTypes,
  iButtonStyles,
  iButtonSizes,
} from '@inscribe/signal';
import {
  CUSTOMER_SIGNAL,
  CUSTOMER_SIGNAL_CONFIG,
} from '@/constants/signals/customer-signals.constants';
import {
  SEVERITY_LEVEL_BY_FRAUD_SIGNAL,
  SEVERITY_LEVEL,
  severityLevelToReadableLabel,
} from '@/constants/signalNames';
import StatusBar from '@/V2/components/status/StatusBar.vue';
import { mapSeverityToStatus } from '@/V2/documents/document-view/document-review-agent/utils';
import CustomerFraudSignalsDataBlock from '@/V2/customers/fraud/CustomerFraudSignalsDataBlock.vue';
import signalConfig from '@/constants/signalConfig';
import CustomerFraudVerificationDetails from '@/V2/customers/fraud/CustomerFraudVerificationDetails.vue';
import CustomerFraudCrossCheckDetails from '@/V2/customers/fraud/CustomerFraudCrossCheckDetails.vue';
export default {
  components: {
    iDrawer,
    StatusBar,
    iButton,
    CustomerFraudSignalsDataBlock,
    CustomerFraudVerificationDetails,
    CustomerFraudCrossCheckDetails,
  },
  props: {
    iDrawerVisible: {
      type: Boolean,
      required: false,
    },
    selectedSignal: {
      type: Object,
    },
    aggregatedDocumentsResults: {
      type: Object,
    },
  },
  data() {
    return {
      CUSTOMER_SIGNAL,
      CUSTOMER_SIGNAL_CONFIG,
      iButtonTypes,
      iButtonStyles,
      iButtonSizes,
      severityLevelToReadableLabel,
      mapSeverityToStatus,
      signalConfig,
      SEVERITY_LEVEL,
    };
  },
  computed: {
    severityOptions() {
      return this.severityLevels.map((severity) => ({
        value: severity,
        text: severityLevelToReadableLabel(severity),
      }));
    },
    selectedSeverity() {
      return this.severityLevels.find(
        (severity) => severity === this.currentSeverityValue,
      );
    },
    isCustomersSignal() {
      return (
        this.selectedSignal?.key ===
        CUSTOMER_SIGNAL.PREVIOUS_HIGH_RISK_APPLICATIONS
      );
    },
    groupedSignals() {
      let groupedSignals = {};
      Object.keys(this.aggregatedDocumentsResults).forEach((key) => {
        const severity = SEVERITY_LEVEL_BY_FRAUD_SIGNAL[key];
        if (!groupedSignals[severity]) {
          groupedSignals[severity] = [];
        }
        const signalObject = signalConfig[key];
        groupedSignals[severity].push({
          ...signalObject,
          title: signalObject?.title || key,
          documents: this.aggregatedDocumentsResults[key]?.details?.documents,
        });
      });
      return groupedSignals;
    },
  },
  methods: {
    hideDrawer() {
      this.$emit('hide-drawer');
    },
    getSignalTitle(typeKey) {
      return signalConfig[typeKey.toLowerCase()]?.title || typeKey;
    },
    getSignalDescription(typeKey) {
      return signalConfig[typeKey.toLowerCase()]?.description || typeKey;
    },
    viewAllDocuments() {
      this.$emit('hide-drawer');
      this.$nextTick(() => {
        this.$router
          .push({
            name: 'customer-documents',
            params: {
              customerId: this.customerId,
            },
          })
          .catch((err) => {});
      });
    },
    viewAllCustomers() {
      this.$emit('hide-drawer');
      this.$nextTick(() => {
        this.$router.push({ name: 'customers-view' });
      });
    },
  },
};
</script>

<template>
  <i-drawer
    :visible="iDrawerVisible"
    class="multiple-severity-signals"
    :title="CUSTOMER_SIGNAL_CONFIG[selectedSignal?.key]?.label"
    @hide-drawer="hideDrawer"
  >
    <div
      id="collect-session-detail"
      class="d-flex flex-column i-gap-9"
      data-testid="customer-fraud-signal-details"
    >
      <status-bar
        v-if="selectedSignal?.highRisk === true"
        :state="mapSeverityToStatus(SEVERITY_LEVEL.HIGH)"
        :label="severityLevelToReadableLabel(SEVERITY_LEVEL.HIGH)"
      />

      <!-- type MULTIPLE_HIGH_RISK_DOCUMENTS -->
      <div
        v-if="
          selectedSignal.key === CUSTOMER_SIGNAL.MULTIPLE_HIGH_RISK_DOCUMENTS
        "
      >
        <customer-fraud-signals-data-block
          title="High-Risk Documents"
          :data="selectedSignal?.value?.details?.documents"
          type="documents"
        />
      </div>

      <!-- type UNUSUAL_TRANSACTION_PATTERNS -->
      <div
        v-if="
          selectedSignal.key === CUSTOMER_SIGNAL.UNUSUAL_TRANSACTION_PATTERNS
        "
      >
        <div class="d-flex flex-column i-gap-9">
          <customer-fraud-signals-data-block
            v-for="typeKey in Object.keys(selectedSignal?.value?.details || {})"
            :key="typeKey"
            :title="getSignalTitle(typeKey)"
            :description="getSignalDescription(typeKey)"
            :data="selectedSignal?.value?.details[typeKey]?.documents"
          />
        </div>
      </div>

      <!-- type MULTIPLE_HIGH_RISK_DOCUMENTS -->
      <div
        v-if="
          selectedSignal.key === CUSTOMER_SIGNAL.MULTIPLE_DOCUMENT_RISK_SIGNALS
        "
      >
        <div class="d-flex flex-column i-gap-9">
          <customer-fraud-signals-data-block
            v-for="signal in groupedSignals[SEVERITY_LEVEL.HIGH]"
            :key="signal.name"
            :title="signal.title"
            :description="signal.tooltipText"
            :info-icon-link-external-url="
              signal.helpArticle
                ? `https://help.inscribe.ai/en/articles/${signal.helpArticle}`
                : null
            "
            :data="signal.documents"
          />
        </div>
      </div>

      <!-- type PREVIOUS_HIGH_RISK_APPLICATIONS -->
      <div
        v-if="
          selectedSignal.key === CUSTOMER_SIGNAL.PREVIOUS_HIGH_RISK_APPLICATIONS
        "
      >
        <customer-fraud-signals-data-block
          title="Previous Applications"
          description="Previous Applications"
          :data="selectedSignal?.value?.details?.customers"
          type="customers"
        />
      </div>

      <!-- verifications -->
      <div v-if="selectedSignal.isVerification">
        <customer-fraud-verification-details
          :signal="selectedSignal"
        ></customer-fraud-verification-details>
      </div>

      <!-- salary cross check -->
      <div
        class="salary-cross-check"
        v-if="selectedSignal.key === CUSTOMER_SIGNAL.SALARY_CROSS_CHECK"
      >
        <customer-fraud-cross-check-details
          :signal="selectedSignal"
        ></customer-fraud-cross-check-details>
      </div>
    </div>

    <template #footer>
      <i-button
        :label="isCustomersSignal ? 'View All Customers' : 'View All Documents'"
        class="view-all-documents-button"
        :type="iButtonTypes.NEUTRAL"
        :button-style="iButtonStyles.SOLID_LIGHT"
        :size="iButtonSizes.LARGE"
        @button-click="
          isCustomersSignal ? viewAllCustomers() : viewAllDocuments()
        "
      />
    </template>
  </i-drawer>
</template>

<style lang="scss">
.multiple-severity-signals {
  .view-all-documents-button {
    width: 100%;
  }
}
</style>
